import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Modal } from '../components/Modal';
import { useToast } from '../components/Toast';
import { fmtKm, fmtMin, formatDate } from '../utils/mockState';
import { loadCostModel, calculateRouteCost, estimateRouteRevenue } from '../utils/costModel';
import { fetchRoadGeometry } from '../utils/osrm';
import api from '../api/client';
import { RouteEntity } from '../types';
import { TILE_URL, TILE_ATTRIBUTION, TILE_MAX_ZOOM } from '../utils/mapTiles';

declare const L: any;

export default function RouteDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [route, setRoute] = useState<RouteEntity | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'stops' | 'details'>('stops');
  const [recalculating, setRecalculating] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const costModel = loadCostModel();

  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const { data } = await api.get<RouteEntity>(`/routes/${id}`);
      setRoute(data);
    } catch {
      setRoute(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const stops = route?.stops || [];

  // Initialize Leaflet detail map with custom teardrop pins
  useEffect(() => {
    if (!mapContainerRef.current || stops.length === 0) return;
    if (typeof L === 'undefined') return;

    if (mapRef.current) {
      mapRef.current.stop();
      mapRef.current.remove();
      mapRef.current = null;
    }

    const first = stops[0].deliveryPoint;
    const map = L.map(mapContainerRef.current, {
      zoomAnimation: false,
      fadeAnimation: false,
      markerZoomAnimation: false
    }).setView([first.latitude, first.longitude], 13);
    L.tileLayer(TILE_URL, { maxZoom: TILE_MAX_ZOOM, attribution: TILE_ATTRIBUTION }).addTo(map);

    const depot = route?.depot;
    const routePoints = depot
      ? [
          ...(route.startsAtDepot ? [{ lat: depot.latitude, lng: depot.longitude }] : []),
          ...stops.map((s) => ({ lat: s.deliveryPoint.latitude, lng: s.deliveryPoint.longitude })),
          ...(route.endsAtDepot ? [{ lat: depot.latitude, lng: depot.longitude }] : [])
        ]
      : stops.map((s) => ({ lat: s.deliveryPoint.latitude, lng: s.deliveryPoint.longitude }));
    const latlngs = routePoints.map((p) => [p.lat, p.lng]);

    const polyline = L.polyline(latlngs, {
      color: '#0f4c5c',
      weight: 5,
      opacity: 0.85
    }).addTo(map);

    if (depot && (route.startsAtDepot || route.endsAtDepot)) {
      const depotRole = route.startsAtDepot && route.endsAtDepot
        ? 'Partida e retorno'
        : route.startsAtDepot
        ? 'Partida'
        : 'Retorno';
      const depotIcon = L.divIcon({
        className: 'custom-depot-pin',
        html: `<div style="background:#0b1b22;color:#fff;width:28px;height:28px;border-radius:8px;display:grid;place-items:center;font-size:11px;font-weight:700;box-shadow:0 4px 12px rgba(0,0,0,0.3);border:2px solid #0f4c5c;">CD</div>`,
        iconSize: [28, 28],
        iconAnchor: [14, 14]
      });
      L.marker([depot.latitude, depot.longitude], { icon: depotIcon }).addTo(map).bindPopup(`<b>${depot.name}</b><br/>${depotRole}: ${depot.address}`);
    }

    stops.forEach((s, idx) => {
      const isOrigin = idx === 0;
      const isDest = idx === stops.length - 1;
      const pinClass = isOrigin ? 'origin' : isDest ? 'destination' : '';

      const icon = L.divIcon({
        className: 'custom-pin-wrapper',
        html: `<div class="marker-pin ${pinClass}"><span>${idx + 1}</span></div>`,
        iconSize: [32, 32],
        iconAnchor: [16, 32]
      });

      L.marker([s.deliveryPoint.latitude, s.deliveryPoint.longitude], { icon })
        .addTo(map)
        .bindPopup(
          `<b>Parada ${idx + 1}: ${s.deliveryPoint.title}</b><br/>${s.deliveryPoint.address}<br/>Janela: ${s.timeWindowStart ? `${s.timeWindowStart}–${s.timeWindowEnd}` : 'Livre'}`
        );
    });

    map.fitBounds(latlngs, { padding: [40, 40] });
    mapRef.current = map;

    let cancelled = false;
    fetchRoadGeometry(routePoints).then((geometry) => {
      if (cancelled || !geometry) return;
      polyline.setLatLngs(geometry);
    });

    return () => {
      cancelled = true;
      // Interrompe animações pendentes antes de desmontar. Sem isso o Leaflet
      // pode executar um callback de zoom em um container já removido.
      map.stop();
      if (mapRef.current === map) {
        map.remove();
        mapRef.current = null;
      }
    };
  }, [stops, route]);

  const handleRecalculate = async () => {
    if (!route) return;
    setRecalculating(true);
    try {
      const { data } = await api.post<RouteEntity>(`/routes/${route.id}/recalculate`);
      setRoute(data);
      showToast('Rota recalculada com sucesso!', 'success');
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Erro ao recalcular rota.', 'error');
    } finally {
      setRecalculating(false);
    }
  };

  const handleStartRoute = async () => {
    if (!route) return;
    if (route.status === 'em_andamento') {
      navigate(`/motorista/${route.id}`);
      return;
    }
    try {
      await api.patch(`/routes/${route.id}`, { status: 'em_andamento' });
      showToast('Rota iniciada! Entrando no Modo Motorista...', 'info');
      navigate(`/motorista/${route.id}`);
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Erro ao iniciar rota.', 'error');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="empty"><p>Carregando rota...</p></div>
      </Layout>
    );
  }

  if (!route) {
    return (
      <Layout>
        <div className="empty">
          <h4>Rota não encontrada</h4>
          <button className="btn btn-secondary" onClick={() => navigate('/rotas')}>
            Voltar para lista
          </button>
        </div>
      </Layout>
    );
  }

  const km = route.distanceM / 1000;
  const durationMin = route.durationS / 60;
  const cost = calculateRouteCost(km, durationMin, stops.length, costModel, route.vehicle?.fuelConsumption);
  const revenue = estimateRouteRevenue(stops.length, costModel);

  return (
    <Layout>
      <div className="route-detail">
        {/* Left: Interactive Map */}
        <div className="route-map-wrap">
          <div ref={mapContainerRef} id="detail-map"></div>

          {/* Floating Metric Overlay */}
          <div className="route-map-overlay">
            <div>
              <div className="label">Distância total</div>
              <div className="val">{fmtKm(km)} km</div>
            </div>
            <div className="sep"></div>
            <div>
              <div className="label">Tempo estimado</div>
              <div className="val">{fmtMin(durationMin)}</div>
            </div>
            <div className="sep"></div>
            <div>
              <div className="label">Economia gerada</div>
              <div className="val" style={{ color: '#0d7a6c' }}>
                -{route.savingsPercent.toFixed(1)}%
              </div>
            </div>
          </div>
        </div>

        {/* Right: Route Details Side Panel */}
        <div className="route-panel">
          <div className="route-panel-head">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div className="name">{route.name}</div>
                <div className="sub">
                  {formatDate(route.date)} · {route.vehicle?.name} ({route.vehicle?.plate})
                </div>
              </div>
              <span
                className={`badge ${
                  route.status === 'em_andamento'
                    ? 'b-amber'
                    : route.status === 'concluida'
                    ? 'b-teal'
                    : route.status === 'cancelada'
                    ? 'b-danger'
                    : 'b-gray'
                }`}
              >
                <span className="dot"></span>
                {route.status === 'em_andamento'
                  ? 'Em andamento'
                  : route.status === 'concluida'
                  ? 'Concluída'
                  : route.status === 'cancelada'
                  ? 'Cancelada'
                  : 'Planejada'}
              </span>
            </div>

            <div className="route-panel-metrics">
              <div className="rp-metric">
                <div className="k">Paradas</div>
                <div className="v">{stops.length}</div>
              </div>
              <div className="rp-metric">
                <div className="k">Motorista</div>
                <div className="v" style={{ fontSize: 13.5, fontWeight: 500 }}>
                  {route.vehicle?.driverName || '—'}
                </div>
              </div>
              <div className="rp-metric">
                <div className="k">CD Base</div>
                <div className="v" style={{ fontSize: 13.5, fontWeight: 500 }}>
                  {route.depot?.name.split(' ')[0] || 'CD'}
                </div>
              </div>
            </div>
          </div>

          <div className="route-panel-tabs">
            <div
              className={`rp-tab ${activeTab === 'stops' ? 'active' : ''}`}
              onClick={() => setActiveTab('stops')}
            >
              Paradas ({stops.length})
            </div>
            <div
              className={`rp-tab ${activeTab === 'details' ? 'active' : ''}`}
              onClick={() => setActiveTab('details')}
            >
              Custos & Detalhes
            </div>
          </div>

          {activeTab === 'stops' ? (
            <div className="route-stops-list">
              {stops.length === 0 && (
                <div className="empty" style={{ padding: 20 }}>
                  <p>Esta rota ainda não tem paradas.</p>
                </div>
              )}
              {stops.map((s, idx) => {
                const isOrigin = idx === 0;
                const isDest = idx === stops.length - 1;

                return (
                  <div
                    key={s.id}
                    className={`rp-stop ${isOrigin ? 'origin' : isDest ? 'destination' : ''}`}
                  >
                    <div className="rp-stop-num">{idx + 1}</div>
                    <div>
                      <div className="a">{s.deliveryPoint.title || s.deliveryPoint.address}</div>
                      <div className="m">
                        <span>{s.deliveryPoint.address}</span>
                        <span className="sep"></span>
                        <span>{s.weightKg} kg</span>
                        {s.timeWindowStart && (
                          <>
                            <span className="sep"></span>
                            <span style={{ color: 'var(--teal-800)', fontWeight: 600 }}>
                              ⏰ Janela: {s.timeWindowStart}–{s.timeWindowEnd}
                            </span>
                          </>
                        )}
                        {s.estimatedTime && (
                          <>
                            <span className="sep"></span>
                            <span style={{ color: 'var(--text-2)', fontWeight: 600 }}>
                              📍 Previsto: {new Date(s.estimatedTime).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </>
                        )}
                        {s.status === 'concluida' && (
                          <>
                            <span className="sep"></span>
                            <span style={{ color: '#0d7a6c', fontWeight: 600 }}>✓ Entregue</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div>
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => window.open(`/tracking/${route.id}`, '_blank')}
                        title="Ver link público de rastreamento da rota"
                        style={{ fontSize: 11, padding: '4px 8px' }}
                      >
                        Tracking ↗
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ padding: 20, overflowY: 'auto' }}>
              <h4 style={{ fontSize: 12, textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: 14 }}>
                Análise Financeira Estimada
              </h4>
              <div className="summary-metric">
                <span className="k">Custo de combustível</span>
                <span className="v mono">R$ {cost.fuel.toFixed(2)}</span>
              </div>
              <div className="summary-metric">
                <span className="k">Custo de mão de obra</span>
                <span className="v mono">R$ {cost.driver.toFixed(2)}</span>
              </div>
              <div className="summary-metric">
                <span className="k">Depreciação & manutenção</span>
                <span className="v mono">R$ {(cost.depreciation + cost.maintenance).toFixed(2)}</span>
              </div>
              <div className="summary-metric">
                <span className="k">Receita média prevista</span>
                <span className="v mono" style={{ color: '#0d7a6c' }}>
                  R$ {revenue.toFixed(2)}
                </span>
              </div>
              <p style={{ fontSize: 11, color: 'var(--text-4)', marginTop: 12 }}>
                Estimativa com base nos parâmetros de custo padrão. Ajuste em Configurações.
              </p>
            </div>
          )}

          <div className="route-panel-foot" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {route.status === 'planejada' && (
              <button
                className="btn btn-secondary"
                onClick={() => navigate(`/rotas/${route.id}/editar`)}
                title="Alterar paradas, veículo, CD ou data desta rota"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                  <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                </svg>
                Editar
              </button>
            )}

            <button
              className="btn btn-secondary"
              onClick={handleRecalculate}
              disabled={recalculating || stops.length === 0}
              title="Recalcular melhor ordem e trajeto"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="23 4 23 10 17 10"></polyline>
                <polyline points="1 20 1 14 7 14"></polyline>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
              </svg>
              {recalculating ? 'Calculando...' : 'Recalcular'}
            </button>

            <button
              className="btn btn-secondary"
              style={{ color: '#0d7a6c', borderColor: '#0d7a6c' }}
              onClick={() => setShowShareModal(true)}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
              </svg>
              Enviar Motorista
            </button>

            <button className="btn btn-primary" onClick={handleStartRoute} style={{ flex: 1, minWidth: 120 }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polygon points="5 3 19 12 5 21 5 3"></polygon>
              </svg>
              {route.status === 'em_andamento' ? 'Modo Motorista' : 'Iniciar Rota'}
            </button>
          </div>
        </div>
      </div>

      {/* Modal Enviar para Motorista */}
      <Modal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        title="Enviar Rota para o Motorista"
        maxWidth="500px"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setShowShareModal(false)}>
              Fechar
            </button>
            <button
              className="btn btn-primary"
              onClick={() => {
                const driverUrl = `${window.location.origin}/motorista/${route?.id}`;
                navigator.clipboard.writeText(driverUrl);
                showToast('Link do motorista copiado para a área de transferência!', 'success');
              }}
            >
              Copiar Link do Motorista
            </button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <p style={{ fontSize: 14, color: 'var(--text-2)' }}>
            Compartilhe o link abaixo com o motorista responsável pela rota <strong>{route?.name}</strong> ({stops.length} paradas). O motorista poderá abrir no celular ou computador para navegar passo a passo, registrar entregas e ocorrências.
          </p>

          <div className="field">
            <label>Link de Acesso do Motorista</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="text"
                className="input"
                readOnly
                value={`${window.location.origin}/motorista/${route?.id}`}
                style={{ fontFamily: 'monospace', fontSize: 13, background: 'var(--paper-2)' }}
                onClick={(e) => (e.target as HTMLInputElement).select()}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
            <a
              href={`https://api.whatsapp.com/send?text=${encodeURIComponent(
                `Olá! Segue o link da sua rota de entregas (${route?.name}) no OtimizaRota:\n${window.location.origin}/motorista/${route?.id}\nBom trabalho!`
              )}`}
              target="_blank"
              rel="noreferrer"
              className="btn btn-secondary"
              style={{ flex: 1, justifyContent: 'center', background: '#25D366', color: '#fff', border: 'none', textDecoration: 'none' }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
              </svg>
              Enviar via WhatsApp
            </a>
          </div>
        </div>
      </Modal>

      {recalculating && (
        <div className="opt-overlay">
          <div className="opt-spinner"></div>
          <div className="opt-msg">Recalculando melhor trajeto...</div>
          <div className="opt-detail">Otimizando ordem e minimizando distâncias</div>
        </div>
      )}
    </Layout>
  );
}
